import { useState, useCallback } from 'react';
import { useConfig } from '../../contexts/ConfigContext';
import { useLog } from '../../contexts/LogContext';
import { api, uploadFile } from '../../utils/api';
import {
  transformCounts,
  transformRoutes,
  transformPages,
} from '../../utils/transforms';
import { JsonFileInput } from '../../components/common/JsonFileInput';
import { FloorPlanPreview } from '../../components/FloorPlanPreview';
import { ActionButtons } from '../../components/common/ActionButtons';
import type {
  CountsJson,
  RoutesJson,
  PageSettings,
} from '../../types/floorPlan.types';
import type { UploadResponse } from '../../utils/api';
import { needsClientProcessing } from '../../utils/compressImage';
import { compressImageOnServer } from '../../utils/compressImageOnServer';
import { MAX_WEBP_DIMENSION } from '../../constants';
import type { FloorPlansContainerProps } from './types';

export const FloorPlansContainer = ({
  projectId,
}: FloorPlansContainerProps) => {
  const { token } = useConfig();
  const { log, clearLog } = useLog();

  const [countsData, setCountsData] = useState<CountsJson | null>(null);
  const [routesData, setRoutesData] = useState<RoutesJson | null>(null);
  const [pageSettingsMap, setPageSettingsMap] = useState<
    Record<number, PageSettings>
  >({});
  const [planName, setPlanName] = useState('');
  const [running, setRunning] = useState(false);

  const [countsFileInfo, setCountsFileInfo] = useState<{
    fileName: string;
    stats: string;
  } | null>(null);
  const [routesFileInfo, setRoutesFileInfo] = useState<{
    fileName: string;
    stats: string;
  } | null>(null);

  // ── Handle counts JSON load ──
  const handleCountsLoaded = useCallback((data: unknown, fileName: string) => {
    const json = data as CountsJson;
    setCountsData(json);
    setPlanName(json.plan_name || '');
    setCountsFileInfo({
      fileName,
      stats: `plan: ${json.plan_name || '?'} | ${json.pages?.length || 0} page(s) | ${json.groups?.length || 0} group(s)`,
    });

    // Initialize page settings
    const settings: Record<number, PageSettings> = {};
    (json.pages || []).forEach((p) => {
      settings[p.index] = {
        scaleFactor: 1,
        quality: 100,
        outputFormat: 'webp',
        file: null,
        pdfFile: null,
        naturalWidth: null,
        naturalHeight: null,
      };
    });
    setPageSettingsMap(settings);
  }, []);

  // ── Handle routes JSON load ──
  const handleRoutesLoaded = useCallback((data: unknown, fileName: string) => {
    const json = data as RoutesJson;
    setRoutesData(json);
    setRoutesFileInfo({
      fileName,
      stats: `plan: ${json.plan_name || '?'} | ${json.routes?.length || 0} route(s)`,
    });
  }, []);

  // ── Update page settings ──
  const handlePageSettingsChange = useCallback(
    (pageIndex: number, update: Partial<PageSettings>) => {
      setPageSettingsMap((prev) => ({
        ...prev,
        [pageIndex]: { ...prev[pageIndex], ...update },
      }));
    },
    [],
  );

  // ── Upload page images to S3 ──
  // Returns a map of pageIndex → { width, height } of the compressed/uploaded image,
  // or null if upload failed.
  const uploadPageImages = useCallback(async (): Promise<Record<
    number,
    { width: number; height: number }
  > | null> => {
    if (!projectId) {
      log('err', 'Project ID is required for image upload');
      return null;
    }

    const currentPlanName = planName || countsData?.plan_name || '';
    if (!currentPlanName) {
      log('err', 'Plan name is required for image upload');
      return null;
    }

    const indices = Object.entries(pageSettingsMap)
      .filter(([, s]) => s.file !== null)
      .map(([idx]) => Number(idx));

    if (indices.length === 0) {
      log('info', 'No image files selected — skipping upload.');
      return {};
    }

    // Validate WebP dimension limits before any server calls
    for (const pageIndex of indices.sort((a, b) => a - b)) {
      const settings = pageSettingsMap[pageIndex];
      if (
        settings.outputFormat === 'webp' &&
        settings.naturalWidth &&
        settings.naturalHeight
      ) {
        const outputWidth = Math.round(
          settings.naturalWidth * settings.scaleFactor,
        );
        const outputHeight = Math.round(
          settings.naturalHeight * settings.scaleFactor,
        );
        if (
          outputWidth > MAX_WEBP_DIMENSION ||
          outputHeight > MAX_WEBP_DIMENSION
        ) {
          log(
            'err',
            `Page ${pageIndex}: WebP output would be ${outputWidth}×${outputHeight} px, exceeding the ${MAX_WEBP_DIMENSION}px WebP limit. Reduce scale factor or switch format.`,
          );
          return null;
        }
      }
    }

    const compressedDimensions: Record<
      number,
      { width: number; height: number }
    > = {};

    log('step', `Uploading ${indices.length} page image(s) to S3...`);

    for (const pageIndex of indices.sort((a, b) => a - b)) {
      const settings = pageSettingsMap[pageIndex];
      const originalFile = settings.file!;
      const { scaleFactor, quality, outputFormat } = settings;

      const willProcess = needsClientProcessing(
        { scaleFactor, quality, outputFormat },
        originalFile.type,
      );

      let fileToUpload: File = originalFile;

      if (willProcess) {
        log(
          'info',
          `  Compressing page ${pageIndex} on server (scale=${scaleFactor}, quality=${quality}, format=${outputFormat})...`,
        );

        try {
          const result = await compressImageOnServer({
            file: originalFile,
            scaleFactor,
            quality,
            outputFormat,
          });
          fileToUpload = result.file;

          compressedDimensions[pageIndex] = {
            width: result.outputWidth,
            height: result.outputHeight,
          };

          log(
            'info',
            `  Page ${pageIndex}: ${result.originalWidth}×${result.originalHeight} → ${result.outputWidth}×${result.outputHeight} | ${(originalFile.size / (1024 * 1024)).toFixed(2)} MB → ${(result.blob.size / (1024 * 1024)).toFixed(2)} MB`,
          );
          if (
            result.requestedWidth !== result.outputWidth ||
            result.requestedHeight !== result.outputHeight
          ) {
            log(
              'warn',
              `  ⚠ Page ${pageIndex}: server auto-downscaled WebP from ${result.requestedWidth}×${result.requestedHeight} to ${result.outputWidth}×${result.outputHeight} (exceeded ${MAX_WEBP_DIMENSION}px limit)`,
            );
          }
        } catch (err) {
          log(
            'err',
            `  Page ${pageIndex} compression failed: ${(err as Error).message}`,
          );
          return null;
        }
      } else {
        // No compression — dimensions equal natural dimensions
        compressedDimensions[pageIndex] = {
          width: settings.naturalWidth ?? 0,
          height: settings.naturalHeight ?? 0,
        };

        log(
          'info',
          `  Uploading page ${pageIndex} as-is: ${originalFile.name} (${(originalFile.size / (1024 * 1024)).toFixed(2)} MB)`,
        );
      }

      const formData = new FormData();
      formData.append('file', fileToUpload);
      formData.append('projectId', projectId);
      formData.append('planName', currentPlanName);
      formData.append('pageIndex', String(pageIndex));

      try {
        const res = await uploadFile<UploadResponse>(
          '/api/quantity-surveyor-floor-plan-pages/upload-page-file',
          formData,
          token,
        );

        if (res.status === 200 || res.status === 201) {
          const s3Key = res.data.s3Key;
          log('ok', `  Page ${pageIndex} uploaded → ${s3Key}`);
        } else {
          log(
            'err',
            `  Page ${pageIndex} failed (${res.status}): ${JSON.stringify(res.data)}`,
          );
          return null;
        }
      } catch (err) {
        log(
          'err',
          `  Page ${pageIndex} upload error: ${(err as Error).message}`,
        );
        return null;
      }
    }

    log('ok', `All ${indices.length} page image(s) uploaded successfully.`);
    return compressedDimensions;
  }, [projectId, planName, countsData, pageSettingsMap, token, log]);

  // ── Upload PDF files to S3 ──
  const uploadPdfFiles = useCallback(async (): Promise<
    Record<number, string>
  > => {
    const currentPlanName = planName || countsData?.plan_name || '';
    const pdfS3Keys: Record<number, string> = {};

    const indices = Object.entries(pageSettingsMap)
      .filter(([, settings]) => settings.pdfFile !== null)
      .map(([index]) => Number(index));

    if (indices.length === 0) return pdfS3Keys;

    log('step', `Uploading ${indices.length} PDF file(s) to S3...`);

    for (const pageIndex of indices.sort((a, b) => a - b)) {
      const pdfFile = pageSettingsMap[pageIndex].pdfFile!;

      const formData = new FormData();
      formData.append('file', pdfFile);
      formData.append('projectId', projectId);
      formData.append('planName', currentPlanName);
      formData.append('pageIndex', String(pageIndex));

      try {
        const res = await uploadFile<UploadResponse>(
          '/api/quantity-surveyor-floor-plan-pages/upload-page-file',
          formData,
          token,
        );

        if (res.status === 200 || res.status === 201) {
          pdfS3Keys[pageIndex] = res.data.s3Key;
          log('ok', `  PDF page ${pageIndex} uploaded → ${res.data.s3Key}`);
        } else {
          log(
            'err',
            `  PDF page ${pageIndex} failed (${res.status}): ${JSON.stringify(res.data)}`,
          );
          throw new Error(`PDF upload failed for page ${pageIndex}`);
        }
      } catch (err) {
        log(
          'err',
          `  PDF page ${pageIndex} upload error: ${(err as Error).message}`,
        );
        throw err;
      }
    }

    log('ok', `All ${indices.length} PDF file(s) uploaded successfully.`);
    return pdfS3Keys;
  }, [projectId, planName, countsData, pageSettingsMap, token, log]);

  // ── Upload images only ──
  const handleUploadOnly = useCallback(async () => {
    if (running) return;
    if (!projectId) return log('err', 'Project ID is required');
    if (!countsData)
      return log(
        'err',
        'Please load a counts JSON file first (needed for plan name)',
      );

    const hasFiles = Object.values(pageSettingsMap).some(
      (s) => s.file !== null || s.pdfFile !== null,
    );
    if (!hasFiles)
      return log(
        'err',
        'No files selected. Select files in the page rows above.',
      );

    setRunning(true);
    clearLog();

    try {
      log('step', 'Upload-only mode: uploading files to S3...');
      const compressedDimensions = await uploadPageImages();
      await uploadPdfFiles();

      if (compressedDimensions !== null) {
        log('ok', '\nDone! Files uploaded successfully.');
      } else {
        log('err', '\nUpload failed. Check the errors above.');
      }
    } catch (err) {
      log('err', `Unexpected error: ${(err as Error).message}`);
    } finally {
      setRunning(false);
    }
  }, [
    running,
    projectId,
    countsData,
    pageSettingsMap,
    clearLog,
    log,
    uploadPageImages,
    uploadPdfFiles,
  ]);

  // ── Execute floor plan create ──
  const handleCreate = useCallback(async () => {
    if (running) return;

    if (!projectId) return log('err', 'Project ID is required');
    if (!countsData) return log('err', 'Please load a counts JSON file');
    if (!routesData) return log('err', 'Please load a routes JSON file');

    setRunning(true);
    clearLog();

    try {
      const currentPlanName =
        planName || countsData.plan_name || 'Unnamed Floor Plan';

      // Validate: every page must have both an image and a PDF
      const pagesWithoutImage = Object.entries(pageSettingsMap)
        .filter(([, s]) => s.file === null)
        .map(([index]) => index);

      const pagesWithoutPdf = Object.entries(pageSettingsMap)
        .filter(([, s]) => s.pdfFile === null)
        .map(([index]) => index);

      if (pagesWithoutImage.length > 0) {
        log(
          'err',
          `Page(s) ${pagesWithoutImage.join(', ')} are missing an image file. Both image and PDF are required for every page.`,
        );
        return;
      }
      if (pagesWithoutPdf.length > 0) {
        log(
          'err',
          `Page(s) ${pagesWithoutPdf.join(', ')} are missing a PDF file. Both image and PDF are required for every page.`,
        );
        return;
      }

      const totalSteps = 3;
      let step = 0;

      // Step 1: Upload images to S3
      step++;
      log('step', `[${step}/${totalSteps}] Uploading page images to S3...`);
      const compressedDimensions = await uploadPageImages();
      if (compressedDimensions === null) {
        log('err', 'Image upload failed — aborting.');
        return;
      }

      // Step 2: Upload PDFs to S3
      step++;
      log('step', `[${step}/${totalSteps}] Uploading PDF files to S3...`);
      let pdfS3Keys: Record<number, string> = {};
      try {
        pdfS3Keys = await uploadPdfFiles();
      } catch {
        log('err', 'PDF upload failed — aborting.');
        return;
      }

      // Step 3: Single import call (creates floor plan + pages + markers + routes in one transaction)
      step++;
      log(
        'step',
        `[${step}/${totalSteps}] Importing floor plan "${currentPlanName}" (single transaction)...`,
      );

      const pagesPayload = transformPages(
        countsData,
        pageSettingsMap,
        projectId,
        currentPlanName,
      );
      const countsPayload = transformCounts(countsData);
      const routesPayload = transformRoutes(routesData);

      const importPayload = {
        projectId: parseInt(projectId, 10),
        name: currentPlanName,
        pages: pagesPayload.pages.map((page, index) => {
          const pageDimensions = compressedDimensions[page.index];

          return {
            index: page.index,
            imagePath: page.image,
            filePath: pdfS3Keys[page.index],
            widthPx:
              page.widthPx || countsData.pages?.[index]?.width_px || 0,
            heightPx:
              page.heightPx || countsData.pages?.[index]?.height_px || 0,
            compressedWidthPx: pageDimensions?.width,
            compressedHeightPx: pageDimensions?.height,
          };
        }),
        groups: countsPayload.groups,
        routes: routesPayload.routes,
      };

      const importRes = await api(
        'POST',
        '/api/quantity-surveyor-floor-plans/import',
        importPayload,
        token,
      );
      if (importRes.status !== 201 && importRes.status !== 200) {
        log(
          'err',
          `  Failed (${importRes.status}): ${JSON.stringify(importRes.data)}`,
        );
        return;
      }

      const newFloorPlanId = (importRes.data as { id: number }).id;
      log('ok', `\nDone! Floor plan ID: ${newFloorPlanId}`);
    } catch (err) {
      log('err', `Unexpected error: ${(err as Error).message}`);
    } finally {
      setRunning(false);
    }
  }, [
    running,
    projectId,
    countsData,
    routesData,
    planName,
    pageSettingsMap,
    token,
    clearLog,
    log,
    uploadPageImages,
    uploadPdfFiles,
  ]);

  return (
    <div>
      <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 mb-4">
        <h2 className="text-sm text-[#8b949e] uppercase tracking-wide mb-3">
          JSON Files
        </h2>
        <div className="flex gap-3 flex-wrap">
          <JsonFileInput
            label="Counts JSON (pages + element groups)"
            placeholder="Drop or click to select counts JSON"
            onFileLoaded={handleCountsLoaded}
            onError={(msg) => log('err', msg)}
            loadedInfo={countsFileInfo}
          />
          <JsonFileInput
            label="Routes JSON"
            placeholder="Drop or click to select routes JSON"
            onFileLoaded={handleRoutesLoaded}
            onError={(msg) => log('err', msg)}
            loadedInfo={routesFileInfo}
          />
        </div>
      </div>
      {countsData && (
        <FloorPlanPreview
          countsData={countsData}
          routesData={routesData}
          pageSettingsMap={pageSettingsMap}
          onPageSettingsChange={handlePageSettingsChange}
          planName={planName}
          onPlanNameChange={setPlanName}
          projectId={projectId}
        />
      )}
      <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 mb-4">
        <ActionButtons
          running={running}
          onUploadFiles={handleUploadOnly}
          onPrimaryAction={handleCreate}
          primaryLabel="Create & Import"
          showPrimary
        />
      </div>
    </div>
  );
};
